<template>
    <div class="mt-3 mb-3">
        <!-- When fullscreen, teleport the player+map to <body> so the overlay
             escapes the app layout's stacking context (otherwise the fixed
             sidebar/toolbar paint over it). Teleport relocates the existing
             DOM without remounting, so the video player keeps all its state. -->
        <teleport
            to="body"
            :disabled="!isFullscreen"
        >
            <div
                class="video-detail-container"
                :class="{ 'is-fullscreen': isFullscreen }"
            >
                <div class="row">
                    <!-- Left panel: Video player + controls -->
                    <div
                        class="col-12 col-lg-6 mb-3 mb-lg-0 pe-lg-2 player-panel"
                        :class="{ 'map-is-collapsed': isFullscreen && mapCollapsed }"
                    >
                        <div class="card p-4 h-100 d-flex flex-column">
                            <!-- Header -->
                            <div class="d-flex align-items-center mb-1">
                                <span
                                    class="trip-color-dot me-2"
                                    :style="{ backgroundColor: trip.color || '#7FC000' }"
                                />
                                <font-awesome-icon
                                    icon="video"
                                    class="me-2 text-muted"
                                />
                                <span class="fw-bold">{{ trip.task }} — {{ formatDateShort(trip.start_time) }}</span>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary ms-auto"
                                    :title="isFullscreen ? $t('video_recordings.exit_fullscreen') : $t('video_recordings.enter_fullscreen')"
                                    :aria-label="isFullscreen ? $t('video_recordings.exit_fullscreen') : $t('video_recordings.enter_fullscreen')"
                                    @click="toggleFullscreen"
                                >
                                    <font-awesome-icon :icon="isFullscreen ? 'compress' : 'expand'" />
                                </button>
                            </div>

                            <div class="d-flex flex-row gap-1 mt-1">
                                <div class="small fw-semibold">
                                    {{ trip.license_plate }} -
                                </div>

                                <div class="small">
                                    {{ trip.driver_name }} -
                                </div>
                                <div class="small">
                                    {{ trip.contract }} -
                                </div>
                                <div class="small mb-3">
                                    {{ formatDistance(trip.length) }} km
                                </div>
                            </div>

                            <!-- Video player -->
                            <video-player
                                ref="videoPlayer"
                                :segments="videoSegments"
                                :loading="videoSegmentsLoading"
                                @time-update="onTimeUpdate"
                                @create-observation="onCreateObservation"
                            />
                        </div>
                    </div>

                    <!-- Right panel: Map + observations -->
                    <div
                        class="col-12 col-lg-6 mb-3 mb-lg-0 ps-lg-2 map-panel"
                        :class="{ 'map-collapsed': isFullscreen && mapCollapsed }"
                    >
                        <div class="card p-4 h-100 d-flex flex-column map-panel-card">
                            <!-- Collapse/expand toggle (fullscreen only). When
                                 collapsed this row stays visible as the handle
                                 to re-open the map; the rest of the card is
                                 hidden via CSS. -->
                            <button
                                v-if="isFullscreen"
                                type="button"
                                class="map-toggle justify-content-end d-flex align-items-center gap-2 mb-2"
                                :title="mapCollapsed ? $t('video_recordings.expand_map') : $t('video_recordings.collapse_map')"
                                :aria-label="mapCollapsed ? $t('video_recordings.expand_map') : $t('video_recordings.collapse_map')"
                                @click="toggleMapCollapsed"
                            >
                                <font-awesome-icon icon="map" />
                                <font-awesome-icon :icon="mapCollapsed ? 'chevron-left' : 'chevron-right'" />
                            </button>

                            <!-- Header -->
                            <div class="map-panel-body d-flex align-items-center justify-content-between mb-1">
                                <div>
                                    <font-awesome-icon
                                        icon="route"
                                        class="me-2"
                                    />
                                    <span class="fw-bold">{{ $t('video_recordings.route_on_map') }}</span>
                                </div>

                                <div class="text-muted small">
                                    <font-awesome-icon
                                        icon="road"
                                        class="me-1"
                                    />
                                    {{ formatDistance(trip.length) }} km
                                </div>
                            </div>

                            <div class="map-panel-body d-flex flex-row justify-content-between mt-2">
                                <div class="text-muted small mb-3">
                                    {{ $t('video_recordings.map_click_hint') }}
                                </div>

                                <div class="dropdown">
                                    <button
                                        class="btn btn-sm btn-outline-secondary dropdown-toggle"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                    >
                                        {{ $t('video_recordings.show_observations') }}
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end">
                                        <li>
                                            <button
                                                class="dropdown-item"
                                                :class="{ active: !showAllObservations }"
                                                type="button"
                                                @click="selectOption(false)"
                                            >
                                                {{ $t('video_recordings.observations_reported_during_trip') }}
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                class="dropdown-item"
                                                :class="{ active: showAllObservations }"
                                                type="button"
                                                @click="selectOption(true)"
                                            >
                                                {{ $t('video_recordings.observations_open_during_trip') }}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <!-- Map -->
                            <div class="detail-map-wrapper map-panel-body border rounded overflow-hidden">
                                <map-container
                                    ref="detailMapContainer"
                                    :find-user="false"
                                    :menu-enabled="false"
                                    @on-map-changed="onMapReady"
                                    @on-marker-tap="onObservationMarkerTap"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>

        <!-- Create observation from the current video frame -->
        <observation-wizard
            v-if="showObservationWizard"
            :coordinates="observationCoordinates"
            :contract="trip.contract_id"
            :initial-photos="observationPhotos"
            :show-thumbnail="true"
            :created-time="observationCreatedTime"
            @close="closeObservationWizard"
            @close-and-update="onWizardSaved"
        />
    </div>
</template>

<script>
/**
 * VideoRecordingsDetail
 * =====================
 * Shows the dashcam video for a trip alongside a map, and keeps a marker on
 * the map synced to the video's playback position (and vice versa).
 *
 * THE CORE PROBLEM: aligning two independent clocks
 * -------------------------------------------------
 * There are two sources of timing that must be lined up:
 *
 *   1. The ROUTE  — `tripDetail.geometry` (lat/lng points) with a parallel
 *      `tripDetail.timestamps` array. These timestamps are genuine UTC and
 *      tell us where the vehicle was at each moment.
 *
 *   2. The VIDEO  — a set of dashcam segments. Each segment has a `recordedAt`
 *      wall-clock time. The video player reports its position as "seconds
 *      elapsed since the first segment started recording".
 *
 * We cannot compare the two clocks in absolute terms because the segment
 * `recordedAt` values are mislabelled by a whole-hour timezone error (the
 * backend tags Finnish local time as "+00:00"). Observed example: route
 * starts 07:56:26 UTC, first segment `recordedAt` says 10:56:31 — exactly
 * 3h 00m 05s apart. The 3h is the timezone bug; the leftover 5s is the real
 * delay between the trip starting and the dashcam beginning to record.
 *
 * THE SOLUTION: align by elapsed time from each clock's own start
 * --------------------------------------------------------------
 * `computeStartDelay()` measures the gap between the video's first
 * `recordedAt` and the route's first timestamp, strips the whole-hour
 * timezone error, and keeps the sub-hour remainder as `startDelaySec` (the
 * genuine "video started N seconds into the trip" delay). Then:
 *
 *   routeElapsed = videoElapsed + startDelaySec
 *
 * maps a video position onto the route, and the inverse maps a clicked route
 * point back to a video position. This is timezone-proof: if the backend is
 * ever fixed to emit true UTC, the whole-hour part rounds to 0 and the same
 * code still yields the correct 5s delay.
 *
 * SMOOTH MOVEMENT: a self-driving animation clock
 * -----------------------------------------------
 * The <video> element only fires `timeupdate` ~4x/second, which is too coarse
 * to move a marker smoothly (it looks like it's jumping in bursts). Instead we
 * run our own requestAnimationFrame loop (~60fps) that advances `playbackElapsed`
 * by real wall-clock time each frame. The video's `timeupdate` events are used
 * only to gently CORRECT that clock toward the true position, not to drive it
 * frame-by-frame. See `animationStep()` for details.
 */
import L from 'leaflet';
import MapContainer from '../map/MapContainer.vue';
import VideoPlayer from './VideoPlayer.vue';
import ObservationWizard from '../observation/ObservationWizard.vue';
import { mapHelper } from '../mixins/MapMixin';
import { catmullRomPosition } from './catmullRom';
import { inject } from 'vue';
import obsSvg from '@/assets/observation.svg';
import obsClosedSvg from '@/assets/observation_closed.svg';


export default {
    name: 'VideoRecordingsDetail',
    components: {
        MapContainer,
        VideoPlayer,
        ObservationWizard
    },
    mixins: [mapHelper],
    props: {
        trip: {
            type: Object,
            required: true
        },
        tripDetail: {
            type: Object,
            default: null
        },
        videoSegments: {
            type: Array,
            default: () => []
        },
        videoSegmentsLoading: {
            type: Boolean,
            default: false
        },
        observations: {
            type: Array,
            default: () => []
        },
        showAllObservations: {
            type: Boolean,
            default: false
        }
    },
    emits: ['marker-selected', 'observation-created', 'toggle-show-all-observations'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data() {
        return {
            // Minimum zoom level to focus on when centering the map on an
            // observation. If the map is already zoomed in at least this far
            // the current zoom is kept; otherwise it zooms in to this level.
            // Adjustable here in one place.
            FOCUS_MIN_ZOOM: 15,
            map: null,
            currentVideoTime: 0,
            positionMarker: null,
            // Current marker lat/lng (where the vehicle was at the frame the
            // user is looking at). Captured when opening the observation flow.
            currentMarkerLat: null,
            currentMarkerLng: null,
            // Observation-from-frame wizard state
            showObservationWizard: false,
            observationCoordinates: [],
            observationPhotos: [],
            observationCreatedTime: null,
            // Self-driving animation clock (seconds elapsed since route start).
            playbackElapsed: 0,      // our current best estimate of position in time
            targetElapsed: 0,        // last value reported/corrected by the video
            isPlaying: false,        // whether to keep advancing the clock
            lastFrameTime: 0,        // performance.now() of previous animation frame
            animationFrameId: null,
            timestampsMs: [],        // cached route timestamps in ms
            routeDurationSec: 0,     // total route span in seconds
            // Seconds to add to the video's elapsed time to line it up with
            // the route. Accounts for the dashcam starting a few seconds after
            // the trip, while ignoring the whole-hour timezone mislabelling of
            // recordedAt. Recomputed whenever the segments/route change.
            startDelaySec: 0,
            // Fullscreen overlay state. Toggling this only flips a CSS class on
            // the already-mounted DOM — the player is never remounted, so
            // playback and all player state survive the toggle.
            isFullscreen: false,
            // Page scroll position captured when entering fullscreen, restored
            // on exit (locking body overflow otherwise resets the scroll).
            savedScrollY: 0,
            // Fullscreen only: collapse the map panel to a thin strip (never
            // fully hidden — the strip stays as the handle to expand it again)
            // so the player can take the full width.
            mapCollapsed: false,
            // Map auto-follow: the map recenters on the moving marker. Manual
            // pan/zoom suspends following until performance.now() passes
            // followSuspendedUntil (0 = not suspended).
            followSuspendedUntil: 0,
            FOLLOW_SUSPEND_MS: 4000,
            // Guard set around our own programmatic panTo so the Leaflet move
            // events it may emit are not mistaken for manual interaction.
            suppressFollowSuspend: false
        }
    },
    watch: {
        trip() {
            this.stopAnimation()
            this.removePositionMarker()
            this.playbackElapsed = 0
            this.targetElapsed = 0
            this.followSuspendedUntil = 0
            if (this.map) {
                this.map.removePolylinesByType(this.TRACE)
                this.map.removeMapItemsByType(this.OBSERVATION)
                this.map.resizeMap()
                this.$nextTick(() => {
                    this.drawRoute()
                    this.drawObservations()
                })
            }
        },
        tripDetail() {
            this.cacheRouteTimestamps()
            if (this.map && this.tripDetail) {
                this.map.removePolylinesByType(this.TRACE)
                this.drawRoute()
            }
        },
        videoSegments() {
            // Segments may arrive after tripDetail (separate request), so
            // recompute the start-delay offset once they're available.
            this.computeStartDelay()
        },
        observations() {
            // Observations arrive from a separate request; (re)draw them when
            // they change. Only relevant once the map exists.
            if (this.map) {
                this.drawObservations()
            }
        }
    },
    mounted() {
        this.cacheRouteTimestamps()
        document.addEventListener('keydown', this.onKeyDown)
    },
    beforeUnmount() {
        this.stopAnimation()
        this.removePositionMarker()
        if (this.map && this.map.map) {
            this.map.map.off('click', this.onMapClick)
            this.map.map.off('dragstart zoomstart', this.onUserMapInteraction)
        }
        // Restore page scrolling in case we are unmounted while fullscreen.
        document.body.classList.remove('video-fullscreen-open')
        document.removeEventListener('keydown', this.onKeyDown)
    },
    methods: {
        // Enter/exit the in-app fullscreen overlay. This only flips a CSS
        // class on the already-mounted layout, so the video player is never
        // remounted and playback/player state is preserved. Lock background
        // page scrolling while open and resize Leaflet once the layout settles
        // (the map's container dimensions change between inline and fullscreen).
        toggleFullscreen() {
            if (this.isFullscreen) {
                this.exitFullscreen()
            } else {
                this.enterFullscreen()
            }
        },

        enterFullscreen() {
            // Remember where the page was scrolled so we can restore it on
            // exit. The app scrolls inside the .routa-content container (not
            // the window — the shell is capped to vh-100 with overflow:auto),
            // so track that element's scrollTop; fall back to the window.
            const scroller = this.getPageScroller()
            this.savedScrollY = scroller ? scroller.scrollTop : window.scrollY
            this.isFullscreen = true
            document.body.classList.add('video-fullscreen-open')
            this.$nextTick(() => {
                if (this.map) this.map.resizeMap()
            })
        },

        exitFullscreen() {
            this.isFullscreen = false
            document.body.classList.remove('video-fullscreen-open')
            // Leaving fullscreen: the collapse state is fullscreen-only, so
            // reset it for next time.
            this.mapCollapsed = false
            // Restore the pre-fullscreen scroll position. This must happen
            // AFTER the teleport re-inserts the detail content inline and the
            // container regains its full height — otherwise setting scrollTop
            // clamps against a short container and lands at the top. $nextTick
            // covers the Vue DOM update; the nested rAF waits for layout.
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const scroller = this.getPageScroller()
                    if (scroller) {
                        scroller.scrollTop = this.savedScrollY
                    } else {
                        window.scrollTo(0, this.savedScrollY)
                    }
                    if (this.map) this.map.resizeMap()
                })
            })
        },

        // The app's scrollable content region. The main layout scrolls inside
        // .routa-content (the shell is fixed to the viewport), so window scroll
        // APIs don't apply. Returns null if not found (then callers fall back
        // to the window).
        getPageScroller() {
            return this.$el && this.$el.closest
                ? this.$el.closest('.routa-content')
                : document.querySelector('.routa-content')
        },

        // Collapse/expand the fullscreen map panel. Collapsed shrinks it to a
        // narrow strip (the strip remains as the expand handle); the player
        // grows to fill the freed width. The layout change is instant (no
        // width transition) so Leaflet can resize to the final width in one
        // step — a transition would leave the map redrawing mid-animation,
        // making the tiles visibly pop in after the panel. resizeMap twice
        // (next tick + a frame later) to catch the settled dimensions.
        toggleMapCollapsed() {
            this.mapCollapsed = !this.mapCollapsed
            this.$nextTick(() => {
                if (this.map) this.map.resizeMap()
                requestAnimationFrame(() => {
                    if (this.map) this.map.resizeMap()
                })
            })
        },

        // Called when the user drags or zooms the map themselves. Suspends
        // auto-follow for a short window so they can look around, then follow
        // resumes on the next marker move. Ignores events caused by our own
        // programmatic panTo (guarded by suppressFollowSuspend).
        onUserMapInteraction() {
            if (this.suppressFollowSuspend) return
            this.followSuspendedUntil = performance.now() + this.FOLLOW_SUSPEND_MS
        },

        // Recenter the map on the marker. Uses panTo (recenter only, zoom
        // preserved) on the raw Leaflet instance — far cheaper than
        // zoomToPosition's invalidateSize()+setView() at ~60fps. No-ops while
        // following is suspended by recent manual interaction.
        followMarker(lat, lng) {
            if (!this.map || !this.map.map) return
            if (performance.now() < this.followSuspendedUntil) return
            this.suppressFollowSuspend = true
            this.map.map.panTo([lat, lng], { animate: false })
            this.suppressFollowSuspend = false
        },

        // Pause already happened in the player. Capture the current frame,
        // snapshot the marker's current coordinate, and open the wizard with
        // the frame pre-attached as a photo.
        async onCreateObservation() {
            const player = this.$refs.videoPlayer
            const frame = player ? await player.captureFrame() : null

            if (!frame) {
                // Tainted canvas (missing CORS headers) or no frame ready.
                this.showAlert(this.$t('video_recordings.capture_failed'))
                return
            }

            // Location = where the vehicle was at this frame. Fall back to the
            // route's first point if the marker hasn't been placed yet.
            let lat = this.currentMarkerLat
            let lng = this.currentMarkerLng
            if ((lat === null || lng === null) &&
                this.tripDetail && this.tripDetail.geometry && this.tripDetail.geometry.length) {
                lat = this.tripDetail.geometry[0].y
                lng = this.tripDetail.geometry[0].x
            }

            if (lat === null || lng === null) {
                this.showAlert(this.$t('video_recordings.capture_failed'))
                return
            }

            this.observationCoordinates = [{ lat, lng }]
            this.observationPhotos = [frame]

            // Compute the real-world timestamp of the current video frame.
            // playbackElapsed = seconds since route start; timestampsMs[0] =
            // absolute route start in ms. The observation should be stamped
            // with the time the vehicle was at this position, not "now."
            // NOTE: sent as a NAIVE LOCAL wall-clock string (no timezone/Z),
            // to match the backend's naive DATETIME column. Trying this format
            // first to see how it lands in the DB.
            if (this.timestampsMs.length > 0) {
                const d = new Date(this.timestampsMs[0] + (this.playbackElapsed * 1000))
                const year = d.getFullYear()
                const month = String(d.getMonth() + 1).padStart(2, '0')
                const day = String(d.getDate()).padStart(2, '0')
                const hours = String(d.getHours()).padStart(2, '0')
                const minutes = String(d.getMinutes()).padStart(2, '0')
                const seconds = String(d.getSeconds()).padStart(2, '0')
                this.observationCreatedTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
            } else {
                this.observationCreatedTime = null
            }

            this.showObservationWizard = true
        },

        // Arrow-key seeking. Left/Right = ±1s, Shift+Left/Right = ±5s. Ignored
        // while typing in a field or while the observation wizard is open, so it
        // never fights text entry or modal interaction. preventDefault stops the
        // key from also panning the Leaflet map / scrolling the page.
        onKeyDown(event) {
            if (event.key === 'Escape' && this.isFullscreen && !this.showObservationWizard && !this.isTypingTarget(event.target)) {
                this.exitFullscreen()
                return
            }
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.code !== 'Space') return
            if (this.showObservationWizard) return
            if (this.isTypingTarget(event.target)) return

            const player = this.$refs.videoPlayer
            if (!player) return

            event.preventDefault()

            const step = event.shiftKey ? 5 : 1

            if (event.key === 'ArrowLeft') {
                player.seekBy(-step)
            }

            if (event.key === 'ArrowRight') {
                player.seekBy(step)
            }

            if (event.code === 'Space') {
                player.isPlaying ? player.pause() : player.play()
            }
        },

        // True when focus is in an element that should receive arrow keys itself.
        isTypingTarget(target) {
            if (!target) return false
            const tag = target.tagName
            return tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                tag === 'SELECT' ||
                target.isContentEditable === true
        },

        closeObservationWizard() {
            this.showObservationWizard = false
            this.observationCoordinates = []
            this.observationPhotos = []
            this.observationCreatedTime = null
        },

        // Wizard reported a successful create/update: close it and ask the
        // parent (which owns the observation data + selected trip) to refetch.
        onWizardSaved() {
            this.closeObservationWizard()
            this.$emit('observation-created')
        },

        // Observation display-mode dropdown. The parent owns the actual
        // showAllObservations state and refetches on change; we just forward
        // the chosen mode (false = reported during trip, true = open during trip).
        selectOption(value) {
            this.$emit('toggle-show-all-observations', value)
        },

        onMapReady(map) {
            this.map = map
            this.map.removePolylinesByType(this.TRACE)
            this.drawRoute()
            this.drawObservations()
            // Attach click handler for seeking video by map click
            if (this.map.map) {
                this.map.map.on('click', this.onMapClick)
                // Suspend auto-follow when the user drags/zooms the map.
                this.map.map.on('dragstart zoomstart', this.onUserMapInteraction)
            }
        },

        // Map marker tap → same sync as the table-row crosshairs: jump the
        // video + marker to that observation's spot on the route.
        onObservationMarkerTap(data) {
            if (!data || data.type !== this.OBSERVATION) return
            const observation = this.observations.find(o => o.id === data.id)
            if (observation) {
                this.seekToObservation(observation)
                // Tell the parent which observation was tapped so the table can
                // highlight the matching row (same result as the crosshairs).
                this.$emit('marker-selected', observation.id)
            }
        },

        // Draw the trip's observations as point markers on the map. Point-type
        // observations are the only kind these dashcam trips produce. Open uses
        // the standard observation icon, closed uses the greyed-out variant.
        // No auto-zoom: markers are drawn within the existing route framing.
        drawObservations() {
            if (!this.map) return
            this.map.removeMapItemsByType(this.OBSERVATION)
            if (!this.observations || !this.observations.length) return

            const markerStore = this.map.getMarkerStore()
            const openIcon = markerStore.getSvgMarker(obsSvg, null, null, '2em', '2em')
            const closedIcon = markerStore.getSvgMarker(obsClosedSvg, null, null, '2em', '2em')

            for (const observation of this.observations) {
                const point = observation.geometry && observation.geometry.point
                if (!point) continue
                const icon = observation.closed_time ? closedIcon : openIcon
                this.map.showMapMarker(observation.id, this.OBSERVATION, point.y, point.x, icon, false, 0)
            }
        },

        drawRoute() {
            if (!this.map) return

            // Use tripDetail geometry (with timestamps) if available, otherwise fall back to trip geometry
            let points = null
            if (this.tripDetail && this.tripDetail.geometry && this.tripDetail.geometry.length > 1) {
                // tripDetail.geometry is already [{x, y}] format
                points = this.tripDetail.geometry
            } else if (this.trip.geometry && this.trip.geometry.points && this.trip.geometry.points.length > 1) {
                points = this.trip.geometry.points.map(p => ({ x: p[0], y: p[1] }))
            }

            if (!points) return

            this.map.drawPolyLine(this.trip.id, this.TRACE, points, '#7FC000', false, false, 5)

            this.$nextTick(() => {
                if (this.map) {
                    this.map.zoomToGroup(this.TRACE, 18)
                }
            })
        },

        cacheRouteTimestamps() {
            if (this.tripDetail && this.tripDetail.timestamps && this.tripDetail.timestamps.length > 1) {
                this.timestampsMs = this.tripDetail.timestamps.map(t => new Date(t).getTime())
                this.routeDurationSec = (this.timestampsMs[this.timestampsMs.length - 1] - this.timestampsMs[0]) / 1000
            } else {
                this.timestampsMs = []
                this.routeDurationSec = 0
            }
            this.computeStartDelay()
        },

        // Figure out how many seconds after the route began the dashcam
        // started recording. recordedAt is mislabelled with a whole-hour
        // timezone error (e.g. Finnish local time tagged as +00:00), so we
        // strip the whole-hour part and keep only the sub-hour remainder as
        // the genuine start delay.
        computeStartDelay() {
            this.startDelaySec = 0
            if (!this.timestampsMs.length) return
            if (!this.videoSegments || !this.videoSegments.length) return

            const firstSeg = [...this.videoSegments].sort((a, b) => a.segmentIndex - b.segmentIndex)[0]
            if (!firstSeg || !firstSeg.recordedAt) return

            const videoStartMs = new Date(firstSeg.recordedAt).getTime()
            const routeStartMs = this.timestampsMs[0]
            const rawDiffSec = (videoStartMs - routeStartMs) / 1000

            // Remove the whole-hour timezone offset; keep the remainder.
            const timezoneErrorSec = Math.round(rawDiffSec / 3600) * 3600
            this.startDelaySec = rawDiffSec - timezoneErrorSec
        },

        // Called on every video timeupdate. We don't move the marker directly
        // here — instead we correct our self-driving clock toward the video's
        // reported time. The animation loop does the actual smooth movement.
        onTimeUpdate(globalTime, elapsedSec) {
            this.currentVideoTime = globalTime
            if (elapsedSec === null || elapsedSec === undefined) return

            // Shift the video's elapsed time onto the route's timeline by the
            // start delay (dashcam started a few seconds after the trip).
            const routeElapsed = elapsedSec + this.startDelaySec
            this.targetElapsed = routeElapsed

            // If our estimate has drifted far from the video (seek, stall,
            // first update), snap; otherwise let the loop ease toward it.
            if (Math.abs(this.playbackElapsed - routeElapsed) > 1.5) {
                this.playbackElapsed = routeElapsed
            }

            this.ensureAnimating()
        },

        // Starts the animation loop if it isn't already running. Safe to call
        // repeatedly — it no-ops when a frame is already scheduled.
        ensureAnimating() {
            if (this.animationFrameId !== null) return
            this.lastFrameTime = performance.now()
            this.animationFrameId = requestAnimationFrame(this.animationStep)
        },

        animationStep() {
            this.animationFrameId = null

            const now = performance.now()
            const deltaSec = (now - this.lastFrameTime) / 1000
            this.lastFrameTime = now

            const player = this.$refs.videoPlayer
            const playing = !!(player && player.isPlaying)
            const rate = player && player.playbackRate ? player.playbackRate : 1

            if (playing) {
                // Advance our own clock by real elapsed time (scaled by speed).
                this.playbackElapsed += deltaSec * rate

                // Gently pull toward the video's last reported time to correct
                // small drift without visible jumps (~10% of the gap per frame).
                const drift = this.targetElapsed - this.playbackElapsed
                this.playbackElapsed += drift * 0.1
            } else {
                // Paused: sit exactly where the video says.
                this.playbackElapsed = this.targetElapsed
            }

            this.renderMarkerAtElapsed(this.playbackElapsed)

            // Keep looping while playing; take one more frame when paused so
            // the marker settles, then stop to save CPU.
            if (playing) {
                this.animationFrameId = requestAnimationFrame(this.animationStep)
            }
        },

        stopAnimation() {
            if (this.animationFrameId !== null) {
                cancelAnimationFrame(this.animationFrameId)
                this.animationFrameId = null
            }
        },

        // Place the marker at the interpolated route position for a given
        // number of seconds elapsed since the route (and video) began.
        renderMarkerAtElapsed(elapsedSec) {
            if (!this.map || !this.map.map) return
            const timestampsMs = this.timestampsMs
            const geometry = this.tripDetail && this.tripDetail.geometry
            if (!geometry || geometry.length < 2 || timestampsMs.length < 2) return

            // Convert elapsed-seconds into an absolute time on the route's
            // clock (route timestamps are absolute ms).
            const currentMs = timestampsMs[0] + (elapsedSec * 1000)

            // Before the route starts or after it ends: pin to the endpoints.
            if (currentMs <= timestampsMs[0]) {
                this.setMarkerPosition(geometry[0].y, geometry[0].x)
                return
            }
            if (currentMs >= timestampsMs[timestampsMs.length - 1]) {
                const last = geometry[geometry.length - 1]
                this.setMarkerPosition(last.y, last.x)
                return
            }

            // Binary search for the pair of consecutive route points whose
            // timestamps bracket currentMs (i.e. timestampsMs[i] <= currentMs
            // < timestampsMs[i+1]). O(log n) since the trip can have thousands
            // of points and this runs every animation frame.
            let searchLow = 0
            let searchHigh = timestampsMs.length - 2
            while (searchLow <= searchHigh) {
                const searchMid = Math.floor((searchLow + searchHigh) / 2)
                if (currentMs < timestampsMs[searchMid]) {
                    searchHigh = searchMid - 1
                } else if (currentMs >= timestampsMs[searchMid + 1]) {
                    searchLow = searchMid + 1
                } else {
                    searchLow = searchMid
                    break
                }
            }

            // The fraction 0..1 of the way from point[pointIndex] to the next
            // point, based on how far currentMs is between their timestamps.
            // Guard against identical timestamps (vehicle stationary — avoids
            // divide-by-zero).
            const pointIndex = searchLow
            const timeBetweenPoints = timestampsMs[pointIndex + 1] - timestampsMs[pointIndex]
            const progressBetweenPoints = timeBetweenPoints > 0
                ? (currentMs - timestampsMs[pointIndex]) / timeBetweenPoints
                : 0

            // Follow a Catmull-Rom curve through the surrounding points rather
            // than a straight line, so the marker glides smoothly around the
            // sparse GPS points instead of kinking at each one. See catmullRom.js.
            const pos = catmullRomPosition(geometry, pointIndex, progressBetweenPoints)

            this.setMarkerPosition(pos.lat, pos.lng)
        },

        setMarkerPosition(lat, lng) {
            this.currentMarkerLat = lat
            this.currentMarkerLng = lng
            const rawMap = this.map.map
            if (!rawMap) return

            if (!this.positionMarker) {
                this.positionMarker = L.circleMarker([lat, lng], {
                    radius: 8,
                    color: '#ffffff',
                    weight: 2,
                    fillColor: '#7FC000',
                    fillOpacity: 1,
                    pane: 'markerPane'
                }).addTo(rawMap)
            } else {
                this.positionMarker.setLatLng([lat, lng])
            }

            // Keep the map centered on the moving marker.
            this.followMarker(lat, lng)
        },

        removePositionMarker() {
            if (this.positionMarker) {
                if (this.map && this.map.map) {
                    this.map.map.removeLayer(this.positionMarker)
                }
                this.positionMarker = null
            }
        },

        // Map click -> video seek. The inverse of the playback->marker sync:
        // find where on the route the user clicked, then jump the video to the
        // frame recorded at that moment.
        onMapClick(e) {
            this.seekToCoordinate(e.latlng.lat, e.latlng.lng)
        },

        // Core seek-by-position: find the route point nearest the given
        // coordinate, snap the playback clock + map marker there, and seek the
        // video to the matching frame. Shared by map clicks and table-row
        // observation clicks so all three sync entry points behave identically.
        // Seeking by position (not by observation timestamp) avoids the
        // observation created_time timezone ambiguity — the route timestamps
        // are the single source of truth for time.
        seekToCoordinate(lat, lng) {
            if (!this.tripDetail || !this.tripDetail.timestamps || !this.tripDetail.geometry) return

            const geometry = this.tripDetail.geometry
            const timestamps = this.tripDetail.timestamps
            if (geometry.length < 2 || timestamps.length < 2) return

            // Find the route point closest to the coordinate. Uses squared
            // distance (no sqrt needed) since we only compare magnitudes.
            let minDist = Infinity
            let nearestIndex = 0
            for (let i = 0; i < geometry.length; i++) {
                const dLat = geometry[i].y - lat
                const dLng = geometry[i].x - lng
                const dist = dLat * dLat + dLng * dLng
                if (dist < minDist) {
                    minDist = dist
                    nearestIndex = i
                }
            }

            // How far into the trip (seconds) that point is.
            const routeStartMs = new Date(timestamps[0]).getTime()
            const pointMs = new Date(timestamps[nearestIndex]).getTime()
            const routeElapsedSec = Math.max(0, (pointMs - routeStartMs) / 1000)

            // Snap our own clock and the marker straight to the point (no
            // gliding), so the marker responds instantly.
            this.playbackElapsed = routeElapsedSec
            this.targetElapsed = routeElapsedSec
            this.renderMarkerAtElapsed(routeElapsedSec)

            // Translate route time back to the video's own timeline (the video
            // starts startDelaySec after the route) and seek there.
            const videoElapsedSec = Math.max(0, routeElapsedSec - this.startDelaySec)
            if (this.$refs.videoPlayer) {
                this.$refs.videoPlayer.seekToElapsed(videoElapsedSec)
            }

            // Focus the map on the observation/coordinate so it is visible even
            // if it was off-screen. Shared by crosshairs click and marker tap.
            this.centerMapOn(lat, lng)
        },

        // Pan the map to the given coordinate. Keep the current zoom if the map
        // is already zoomed in at least FOCUS_MIN_ZOOM; otherwise zoom in to
        // FOCUS_MIN_ZOOM so the point is clearly visible.
        centerMapOn(lat, lng) {
            if (!this.map || lat === null || lat === undefined || lng === null || lng === undefined) return
            const currentZoom = this.map.getMapZoomLevel()
            const targetZoom = Math.max(currentZoom, this.FOCUS_MIN_ZOOM)
            this.map.zoomToPosition(lat, lng, targetZoom)
        },

        // Table-row observation click: jump the video + map marker to the
        // observation's location on the route. Point observations only.
        seekToObservation(observation) {
            if (!observation || !observation.geometry || !observation.geometry.point) return
            const point = observation.geometry.point
            this.seekToCoordinate(point.y, point.x)
        },

        formatDateShort(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            if (isNaN(date.getTime())) return '-'
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}.${month}.${year}`
        },

        formatDistance(km) {
            if (km === null || km === undefined) return '0'
            return Number(km).toFixed(1)
        }
    }
}
</script>

<style scoped>
.trip-color-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
}

.detail-map-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    aspect-ratio: 16 / 9;
}

/* Fullscreen overlay: fills the browser viewport above the app chrome but
   below modals (BaseModal mask is 9998), so the observation wizard still
   opens on top. Only a class toggle — the player DOM is never remounted. */
.video-detail-container.is-fullscreen {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: #000;
    padding: 0.5rem;
    overflow: hidden;
}

.video-detail-container.is-fullscreen > .row {
    height: 100%;
    margin: 0;
    flex-wrap: nowrap;
}

/* Player ~2/3, map ~1/3, both full height. */
.video-detail-container.is-fullscreen > .row > :first-child {
    flex: 0 0 66.6667%;
    max-width: 66.6667%;
    height: 100%;
}

.video-detail-container.is-fullscreen > .row > :last-child {
    flex: 0 0 33.3333%;
    max-width: 33.3333%;
    height: 100%;
}

.video-detail-container.is-fullscreen .card {
    height: 100%;
    overflow: hidden;
}

/* Kill horizontal padding on the columns in fullscreen so the player's grown
   width (calc) can't overflow the row and spawn a horizontal scrollbar. A
   small gap on the map column keeps the two panels visually separated. */
.video-detail-container.is-fullscreen > .row > .player-panel,
.video-detail-container.is-fullscreen > .row > .map-panel {
    padding-left: 0;
    padding-right: 0;
    margin-bottom: 0;
}

.video-detail-container.is-fullscreen > .row > .map-panel {
    padding-left: 0.5rem;
}

/* In fullscreen the map fills its column instead of keeping the 16:9 box. */
.video-detail-container.is-fullscreen .detail-map-wrapper {
    aspect-ratio: auto;
    height: 100%;
    flex: 1 1 auto;
}

/* --- Collapsible map panel (fullscreen only) ---------------------------- */

/* The toggle row is only shown in fullscreen (v-if). When the map is collapsed
   it stays visible as the handle to re-open the map. */
.map-toggle {
    background: transparent;
    border: none;
    padding: 0;
    color: #404041;
    cursor: pointer;
}

.map-toggle:hover {
    color: #000;
}

/* Collapsed: the map column shrinks to a narrow strip and the player column
   grows to fill the freed width. The card stays mounted; only its body
   (header, hint, map) is hidden — the toggle row remains as the handle. The
   strip is wide enough to keep the full p-4 padding on all sides for visual
   consistency with the expanded panel. */
.video-detail-container.is-fullscreen > .row > .map-panel.map-collapsed {
    flex: 0 0 88px;
    max-width: 88px;
    min-width: 88px;
}

/* When the map is collapsed, the player takes everything except the strip. */
.video-detail-container.is-fullscreen > .row > .player-panel.map-is-collapsed {
    flex: 1 1 auto;
    max-width: calc(100% - 88px);
}

/* Hide the map card's body while collapsed; keep the toggle handle. Uses
   !important because Bootstrap's .d-flex utility on these rows sets
   display: flex !important, which would otherwise win over display: none. */
.video-detail-container.is-fullscreen .map-panel.map-collapsed .map-panel-body {
    display: none !important;
}

.video-detail-container.is-fullscreen .map-panel.map-collapsed .map-toggle {
    width: 100%;
    justify-content: center;
    gap: 0.35rem;
}

/* Mobile/tablet: stack the player above the map. */
@media (max-width: 768px) {
    .video-detail-container.is-fullscreen > .row {
        flex-wrap: wrap;
        overflow: auto;
    }

    .video-detail-container.is-fullscreen > .row > :first-child,
    .video-detail-container.is-fullscreen > .row > :last-child {
        flex: 0 0 100%;
        max-width: 100%;
        height: auto;
    }

    .video-detail-container.is-fullscreen .detail-map-wrapper {
        height: 40vh;
    }
}
</style>

<!-- Global (non-scoped): lock background page scroll while the video player
     fullscreen overlay is open. Targets <body>, so it cannot be scoped. -->
<style>
body.video-fullscreen-open {
    overflow: hidden;
}

/* Reach into the VideoPlayer's scoped layout from the fullscreen ancestor.
   The player's video box uses a width-driven 16:9 aspect-ratio; in fullscreen
   (and especially once the map is collapsed and the player is very wide) that
   would make it taller than the panel and spawn a scrollbar. Here we let the
   player fill the panel HEIGHT instead, and the video (object-fit: contain)
   scales within it. */
.video-detail-container.is-fullscreen .video-player {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.video-detail-container.is-fullscreen .video-player .video-container {
    aspect-ratio: auto;
    flex: 1 1 auto;
    min-height: 0;
}
</style>
